import type {
	ColorTokenVariant,
	ComponentStateType,
	SizeType,
} from "@scaleaq/sui-react/dist/models/types";
import { Link } from "@tanstack/react-router";
import clsx from "clsx";
import type { ComponentProps } from "react";
import { COMPONENT_STATE } from "src/models/constants";
import type { ColorTokenRole } from "src/models/types/common.types";
import type { Merge } from "src/models/utility";
import type { FlexAlignerProps } from "../FlexAligner";
import { Icon, type IconProps } from "../Icon/Icon";
import styling from "./Button.module.scss";

export namespace Button {
	export type ElementName = "button" | "a";
	export type ElementType = HTMLElementTagNameMap[ElementName];
	export type PaddingRule =
		| "text-only"
		| "icon-only"
		| "icon-and-text"
		| "icon-left-and-text"
		| "icon-right-and-text";
	export type Props = Merge<
		Omit<ComponentProps<"button">, "disabled">,
		{
			size?: Extract<SizeType, "xsmall" | "small" | "medium" | "large">;
			state?: ComponentStateType;
			href?: string;
			color?: Extract<
				ColorTokenRole,
				"action" | "neutral" | "success" | "caution" | "critical" | "warning" | "alarm"
			>;
			variant?: ColorTokenVariant;
			text?: string;
			leftIcon?: IconProps | React.ReactNode;
			rightIcon?: IconProps | React.ReactNode;
			radiusType?: "rounded" | "pill" | "curved" | "square";
			alignment?: Extract<FlexAlignerProps["alignment"], "center-left" | "center-right" | "center">;
			paddingRule?: PaddingRule;
		}
	>;
}

export const Button = (props: Button.Props) => {
	// *****************************************
	// #region Properties
	// *****************************************

	const {
		radiusType = "curved",
		alignment = "center-left",
		color = "action",
		variant = "solid",
		state = "default",
		size = "medium",
		paddingRule = "text-only",
		text,
		leftIcon,
		rightIcon,
		children,
		href,
		className,
		onClick,
		ref,
		...rest
	} = props;

	// *****************************************
	// #endregion
	// *****************************************

	// *****************************************
	// #region Handling
	// *****************************************

	const _isLoading = () => {
		return state === COMPONENT_STATE.loading;
	};

	const _isDisabled = () => {
		return state === COMPONENT_STATE.disabled || _isLoading();
	};

	const _isActive = () => {
		return state === COMPONENT_STATE.active && !_isDisabled();
	};

	const _getSizeClasses = () => {
		let _temp: Button.PaddingRule = paddingRule;
		const hasChildren: boolean = !!(children || text?.length);

		if (leftIcon && rightIcon && hasChildren) {
			_temp = "icon-and-text";
		} else if (leftIcon && !rightIcon && hasChildren) {
			_temp = "icon-left-and-text";
		} else if (rightIcon && !leftIcon && hasChildren) {
			_temp = "icon-right-and-text";
		} else if ((leftIcon || rightIcon) && hasChildren) {
			_temp = "icon-only";
		}

		return clsx(styling.size, {
			[styling[`size__${size}`]]: size,
			[styling[`size__${size}--${_temp}`]]: _temp,
		});
	};

	const _getClasses = () => {
		return clsx(
			styling.button,
			{
				[styling[`color-${color}`]]: !!color,
				[styling[`color-${color}--${variant}`]]: !_isDisabled(),
				[styling[`color-${color}--${variant}--active`]]: _isActive(),
				[styling[`disabled--${variant}`]]: _isDisabled(),
				[styling[`radius--${radiusType}`]]: radiusType,
				[styling[`alignment--${alignment}`]]: alignment,
				[`${className}`]: className,
			},
			_getSizeClasses(),
		);
	};

	// *****************************************
	// #endregion
	// *****************************************

	// *****************************************
	// #region Render
	// *****************************************

	const _renderLeftIcon = () => {
		if (leftIcon) {
			if (typeof leftIcon === "object" && typeof (leftIcon as IconProps).name === "string") {
				return <Icon {...(leftIcon as IconProps)} />;
			} else {
				return <>{leftIcon}</>;
			}
		}
	};

	const _renderRightIcon = () => {
		if (rightIcon) {
			if (typeof rightIcon === "object" && typeof (rightIcon as IconProps).name === "string") {
				return (
					<div className={styling["right-icon-wrapper"]}>{<Icon {...(rightIcon as IconProps)} />}</div>
				);
			} else {
				return <div className={styling["right-icon-wrapper"]}>{rightIcon as any}</div>;
			}
		}
	};

	const _renderContent = () => {
		return (
			<>
				{_renderLeftIcon()}
				{text}
				{children}
				{_renderRightIcon()}
			</>
		);
	};

	if (href) {
		return (
			<Link
				onClick={() => {
					if (onClick) {
						onClick({} as any);
					}
				}}
				to={href}
				data-loading={_isLoading()}
				data-active={_isActive()}
				data-disabled={_isDisabled()}
				className={_getClasses()}
				ref={ref as any}
			>
				{_renderContent()}
			</Link>
		);
	}

	return (
		<button
			data-loading={_isLoading()}
			data-active={_isActive()}
			data-disabled={_isDisabled()}
			disabled={_isDisabled()}
			onClick={_isDisabled() ? undefined : onClick}
			className={_getClasses()}
			ref={ref}
			{...rest}
		>
			{_renderContent()}
		</button>
	);

	// *****************************************
	// #endregion
	// *****************************************
};
